import React, { useState } from 'react';
import { 
  FaCalculator, FaUser, FaWeight, FaRuler, 
  FaFire, FaHeart, FaTrophy, FaYoutube, FaBell, FaPlay,
  FaStar, FaUsers, FaDownload, FaFilePdf
} from 'react-icons/fa';
import channelLogo from '../assets/channels4_profile.jpg';
import transformPhoto from '../assets/transform.png';
import { generateBMIReport } from '../utils/pdfGenerator';

const GymManagement = () => {
  const [activeTab, setActiveTab] = useState('bmi');
  const [bmiData, setBmiData] = useState({
    weight: '',
    height: '',
    age: '',
    gender: 'male',
    activityLevel: 'moderate'
  });
  const [bmiResult, setBmiResult] = useState(null);

  // BMI Calculation
  const calculateBMI = () => {
    const weight = parseFloat(bmiData.weight);
    const height = parseFloat(bmiData.height) / 100; // Convert cm to meters
    
    if (weight && height) {
      const bmi = weight / (height * height);
      const category = getBMICategory(bmi);
      const idealWeight = getIdealWeight(height, bmiData.gender);
      const bmr = calculateBMR(weight, height * 100, bmiData.age, bmiData.gender);
      const calories = calculateCalories(bmr, bmiData.activityLevel);
      
      setBmiResult({
        bmi: bmi.toFixed(1),
        category,
        idealWeight,
        bmr: Math.round(bmr),
        calories: Math.round(calories),
        weightDiff: (weight - idealWeight).toFixed(1)
      });
    }
  };

  const getBMICategory = (bmi) => {
    if (bmi < 18.5) return { text: 'Underweight', color: 'text-blue-600', bg: 'bg-blue-100' };
    if (bmi < 25) return { text: 'Normal', color: 'text-green-600', bg: 'bg-green-100' };
    if (bmi < 30) return { text: 'Overweight', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    return { text: 'Obese', color: 'text-red-600', bg: 'bg-red-100' };
  };

  const getIdealWeight = (height, gender) => {
    // Robinson formula
    const heightCm = height * 100;
    if (gender === 'male') {
      return 52 + 1.9 * ((heightCm - 152.4) / 2.54);
    } else {
      return 49 + 1.7 * ((heightCm - 152.4) / 2.54);
    }
  };

  const calculateBMR = (weight, height, age, gender) => {
    // Mifflin-St Jeor Equation
    if (gender === 'male') {
      return 10 * weight + 6.25 * height - 5 * age + 5;
    } else {
      return 10 * weight + 6.25 * height - 5 * age - 161;
    }
  };

  const calculateCalories = (bmr, activityLevel) => {
    const multipliers = {
      sedentary: 1.2,
      light: 1.375,
      moderate: 1.55,
      active: 1.725,
      extra: 1.9
    };
    return bmr * multipliers[activityLevel];
  };

  const handleInputChange = (e) => {
    setBmiData({
      ...bmiData,
      [e.target.name]: e.target.value
    });
  };

  const handleDownloadPDF = () => {
    if (bmiResult) {
      generateBMIReport(bmiResult, bmiData);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900">
      {/* Hero Header with Channel Branding */}
      <div className="relative bg-gradient-to-r from-red-600 via-purple-600 to-indigo-600 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 items-center">
            
            {/* Channel Logo & Info */}
            <div className="text-center lg:text-left">
              <div className="flex justify-center lg:justify-start mb-4 sm:mb-6">
                <div className="relative">
                  <img
                    src={channelLogo}
                    alt="Rugged Fitness Coach Sabari"
                    className="w-24 h-24 sm:w-32 sm:h-32 rounded-full border-4 border-white shadow-2xl"
                  />
                  <div className="absolute -bottom-2 -right-2 bg-red-600 text-white p-2 rounded-full">
                    <FaYoutube size={20} />
                  </div>
                </div>
              </div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">Rugged Fitness Coach Sabari</h1>
              <p className="text-lg sm:text-xl text-red-100 mb-4">💪 Rugged Training • Powerful Results</p>
              <div className="flex flex-wrap justify-center lg:justify-start gap-2 sm:gap-4 mb-4 sm:mb-6">
                <div className="bg-white/20 backdrop-blur-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded-full">
                  <span className="text-white font-bold text-sm sm:text-base">🔥 Rugged Workouts</span>
                </div>
                <div className="bg-white/20 backdrop-blur-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded-full">
                  <span className="text-white font-bold text-sm sm:text-base">🥗 Power Nutrition</span>
                </div>
              </div>
              
              {/* Channel Stats */}
              <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6">
                <div className="text-center">
                  <div className="text-xl sm:text-2xl font-bold text-white">380+</div>
                  <div className="text-red-200 text-xs sm:text-sm">Subscribers</div>
                </div>
                <div className="text-center">
                  <div className="text-xl sm:text-2xl font-bold text-white">80+</div>
                  <div className="text-red-200 text-xs sm:text-sm">Videos</div>
                </div>
                <div className="text-center">
                  <div className="text-xl sm:text-2xl font-bold text-white">94,245 +</div>
                  <div className="text-red-200 text-xs sm:text-sm">Views</div>
                </div>
              </div>

              {/* Subscribe Button */}
              <a 
                href="https://www.youtube.com/@Fitness_coach_129" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-red-600 hover:bg-red-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg transition-all transform hover:scale-105 inline-flex items-center justify-center space-x-2 sm:space-x-3 shadow-2xl w-full sm:w-auto"
              >
                <FaYoutube size={24} />
                <span>SUBSCRIBE NOW</span>
                <FaBell />
              </a>
            </div>

            {/* Transformation Photo */}
            <div className="flex justify-center">
              <div className="relative">
                <img
                  src={transformPhoto}
                  alt="Fitness Transformation"
                  className="w-64 h-80 sm:w-80 sm:h-96 object-cover rounded-2xl shadow-2xl border-4 border-white/20"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-2xl"></div>
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <h3 className="text-lg sm:text-xl font-bold mb-2">🔥 Your Transformation Awaits!</h3>
                  <p className="text-xs sm:text-sm opacity-90">Join thousands who transformed their lives</p>
                </div>
                
                {/* Floating Achievement Badges */}
                <div className="absolute -top-4 -right-4 bg-yellow-500 text-black p-3 rounded-full shadow-lg animate-bounce">
                  <FaTrophy size={20} />
                </div>
                <div className="absolute -bottom-4 -left-4 bg-green-500 text-white p-3 rounded-full shadow-lg animate-pulse">
                  <FaStar size={20} />
                </div>
              </div>
            </div>

            {/* Motivational Content */}
            <div className="text-center lg:text-right">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20">
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">🚀 Start Your Rugged Journey</h2>
                <div className="space-y-3 sm:space-y-4 text-left">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">1</span>
                    </div>
                    <span className="text-white text-sm sm:text-base">Calculate your BMI below</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">2</span>
                    </div>
                    <span className="text-white text-sm sm:text-base">Get personalized workout plan</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">3</span>
                    </div>
                    <span className="text-white text-sm sm:text-base">Follow our YouTube videos</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white font-bold text-sm">4</span>
                    </div>
                    <span className="text-white text-sm sm:text-base">Transform your body with rugged training!</span>
                  </div>
                </div>
                
                <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-lg">
                  <p className="text-yellow-200 font-bold text-xs sm:text-sm">
                    💡 "Rugged training builds rugged character. Your mind is your strongest muscle!"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-10 left-10 w-4 h-4 bg-yellow-400 rounded-full animate-ping opacity-75"></div>
        <div className="absolute bottom-10 right-10 w-6 h-6 bg-pink-500 rounded-full animate-bounce opacity-75"></div>
        <div className="absolute top-1/2 left-20 w-3 h-3 bg-green-400 rounded-full animate-pulse opacity-75"></div>
      </div>

      {/* Navigation Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-6 sm:mb-8">
          {[
            { id: 'bmi', label: 'BMI Calculator', icon: FaCalculator, color: 'from-green-500 to-emerald-600' },
            { id: 'members', label: 'Community', icon: FaUsers, color: 'from-blue-500 to-cyan-600' },
            { id: 'workouts', label: 'Workouts', icon: FaFire, color: 'from-red-500 to-pink-600' },
            { id: 'nutrition', label: 'Nutrition', icon: FaHeart, color: 'from-purple-500 to-violet-600' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 sm:space-x-3 px-4 sm:px-6 py-3 sm:py-4 rounded-xl font-bold transition-all transform hover:scale-105 text-sm sm:text-base ${
                activeTab === tab.id
                  ? `bg-gradient-to-r ${tab.color} text-white shadow-2xl`
                  : 'bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm'
              }`}
            >
              <tab.icon size={18} className="sm:w-5 sm:h-5" />
              <span className="hidden xs:inline">{tab.label}</span>
              <span className="xs:hidden">{tab.label.split(' ')[0]}</span>
            </button>
          ))}
        </div>

        {/* BMI Calculator Tab */}
        {activeTab === 'bmi' && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            {/* BMI Input Form */}
            <div className="xl:col-span-1">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-6 lg:p-8 border border-white/20 shadow-2xl">
                <div className="flex items-center space-x-3 mb-4 sm:mb-6">
                  <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-2 sm:p-3 rounded-full">
                    <FaCalculator className="text-white text-lg sm:text-xl" />
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-white">BMI Calculator</h2>
                    <p className="text-gray-300 text-xs sm:text-sm">Get your personalized health metrics</p>
                  </div>
                </div>

                <div className="space-y-4 sm:space-y-6">
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-white font-medium mb-2 text-sm sm:text-base">
                        <FaWeight className="inline mr-2 text-green-400" />
                        Weight (kg)
                      </label>
                      <input
                        type="number"
                        name="weight"
                        value={bmiData.weight}
                        onChange={handleInputChange}
                        placeholder="Enter Your Weight"
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/20 border border-white/30 rounded-xl text-white placeholder-gray-300 focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-400/20 transition-all text-sm sm:text-base"
                      />
                    </div>
                    <div>
                      <label className="block text-white font-medium mb-2 text-sm sm:text-base">
                        <FaRuler className="inline mr-2 text-blue-400" />
                        Height (cm)
                      </label>
                      <input
                        type="number"
                        name="height"
                        value={bmiData.height}
                        onChange={handleInputChange}
                        placeholder="Enter Your Height"
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/20 border border-white/30 rounded-xl text-white placeholder-gray-300 focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all text-sm sm:text-base"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-white font-medium mb-2 text-sm sm:text-base">Age</label>
                      <input
                        type="number"
                        name="age"
                        value={bmiData.age}
                        onChange={handleInputChange}
                        placeholder="Enter Your Age"
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/20 border border-white/30 rounded-xl text-white placeholder-gray-300 focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-400/20 transition-all text-sm sm:text-base"
                      />
                    </div>
                    <div>
                      <label className="block text-white font-medium mb-2 text-sm sm:text-base">Gender</label>
                      <select
                        name="gender"
                        value={bmiData.gender}
                        onChange={handleInputChange}
                        className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/20 border border-white/30 rounded-xl text-white focus:outline-none focus:border-pink-400 focus:ring-2 focus:ring-pink-400/20 transition-all text-sm sm:text-base"
                      >
                        <option value="male" className="bg-gray-800">Male</option>
                        <option value="female" className="bg-gray-800">Female</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-white font-medium mb-2 text-sm sm:text-base">Activity Level</label>
                    <select
                      name="activityLevel"
                      value={bmiData.activityLevel}
                      onChange={handleInputChange}
                      className="w-full px-3 sm:px-4 py-2 sm:py-3 bg-white/20 border border-white/30 rounded-xl text-white focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 transition-all text-sm sm:text-base"
                    >
                      <option value="sedentary" className="bg-gray-800">Sedentary (little/no exercise)</option>
                      <option value="light" className="bg-gray-800">Light (light exercise 1-3 days/week)</option>
                      <option value="moderate" className="bg-gray-800">Moderate (moderate exercise 3-5 days/week)</option>
                      <option value="active" className="bg-gray-800">Active (hard exercise 6-7 days/week)</option>
                      <option value="extra" className="bg-gray-800">Extra Active (very hard exercise, physical job)</option>
                    </select>
                  </div>

                  <button
                    onClick={calculateBMI}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold py-3 sm:py-4 rounded-xl transition-all transform hover:scale-105 shadow-2xl text-sm sm:text-base"
                  >
                    🔥 Calculate My Fitness Metrics
                  </button>
                </div>
              </div>
            </div>

            {/* BMI Results */}
            <div className="xl:col-span-2 space-y-6">
              {bmiResult ? (
                <>
                  {/* BMI Score Card */}
                  <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-8 text-white shadow-2xl">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                      <div className="text-center md:text-left">
                        <div className="flex flex-col sm:flex-row items-center justify-between mb-4 gap-3">
                          <div>
                            <h3 className="text-2xl font-bold">BMI Analysis Results</h3>
                            {/* <p className="text-indigo-200 text-xl font-semibold mt-2">Your BMI: {bmiResult.bmi}</p> */}
                          </div>
                          <button
                            onClick={handleDownloadPDF}
                            className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg font-medium transition-all flex items-center space-x-2 backdrop-blur-sm whitespace-nowrap"
                            title="Download BMI Report as PDF"
                          >
                            <FaFilePdf />
                            <span>Download PDF</span>
                          </button>
                        </div>
                        <div className="text-8xl font-bold mb-4 bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text">
                          {bmiResult.bmi}
                        </div>
                        <div className={`inline-block px-6 py-3 rounded-full ${bmiResult.category.bg} ${bmiResult.category.color} font-bold text-lg`}>
                          {bmiResult.category.text}
                        </div>
                      </div>
                      
                      <div className="space-y-4">
                        <div className="bg-white/20 backdrop-blur-sm p-4 rounded-xl">
                          <div className="flex justify-between items-center">
                            <span className="text-indigo-100">Ideal Weight</span>
                            <span className="text-white font-bold text-xl">{bmiResult.idealWeight.toFixed(1)} kg</span>
                          </div>
                        </div>
                        <div className="bg-white/20 backdrop-blur-sm p-4 rounded-xl">
                          <div className="flex justify-between items-center">
                            <span className="text-indigo-100">Weight Difference</span>
                            <span className={`font-bold text-xl ${parseFloat(bmiResult.weightDiff) > 0 ? 'text-red-300' : 'text-green-300'}`}>
                              {bmiResult.weightDiff > 0 ? '+' : ''}{bmiResult.weightDiff} kg
                            </span>
                          </div>
                        </div>
                        <div className="bg-white/20 backdrop-blur-sm p-4 rounded-xl">
                          <div className="flex justify-between items-center">
                            <span className="text-indigo-100">Daily Calories</span>
                            <span className="text-yellow-300 font-bold text-xl">{bmiResult.calories} cal</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Personalized Recommendations */}
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 shadow-2xl">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
                      <h3 className="text-xl sm:text-2xl font-bold text-white flex items-center">
                        <FaTrophy className="mr-3 text-yellow-400" />
                        Your Personalized Fitness Plan
                      </h3>
                      
                      {/* PDF Download Button */}
                      <button
                        onClick={handleDownloadPDF}
                        className="bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl font-bold transition-all transform hover:scale-105 flex items-center space-x-2 shadow-lg whitespace-nowrap"
                      >
                        <FaDownload />
                        <span>Download Full Report</span>
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <h4 className="text-lg font-bold text-yellow-400">🎯 Recommendations</h4>
                        <div className="space-y-3 text-gray-300">
                          {bmiResult.category.text === 'Underweight' && (
                            <>
                              <div className="flex items-start space-x-3">
                                <div className="w-2 h-2 bg-green-400 rounded-full mt-2"></div>
                                <p>Focus on strength training and muscle building</p>
                              </div>
                              <div className="flex items-start space-x-3">
                                <div className="w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                                <p>Increase caloric intake with healthy foods</p>
                              </div>
                              <div className="flex items-start space-x-3">
                                <div className="w-2 h-2 bg-purple-400 rounded-full mt-2"></div>
                                <p>Consider protein supplements</p>
                              </div>
                            </>
                          )}
                          {bmiResult.category.text === 'Normal' && (
                            <>
                              <div className="flex items-start space-x-3">
                                <div className="w-2 h-2 bg-green-400 rounded-full mt-2"></div>
                                <p>Maintain current weight with balanced diet</p>
                              </div>
                              <div className="flex items-start space-x-3">
                                <div className="w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                                <p>Mix cardio and strength training</p>
                              </div>
                              <div className="flex items-start space-x-3">
                                <div className="w-2 h-2 bg-purple-400 rounded-full mt-2"></div>
                                <p>Focus on overall fitness and health</p>
                              </div>
                            </>
                          )}
                          {(bmiResult.category.text === 'Overweight' || bmiResult.category.text === 'Obese') && (
                            <>
                              <div className="flex items-start space-x-3">
                                <div className="w-2 h-2 bg-red-400 rounded-full mt-2"></div>
                                <p>Create a caloric deficit for weight loss</p>
                              </div>
                              <div className="flex items-start space-x-3">
                                <div className="w-2 h-2 bg-orange-400 rounded-full mt-2"></div>
                                <p>Increase cardio exercises</p>
                              </div>
                              <div className="flex items-start space-x-3">
                                <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2"></div>
                                <p>Focus on whole foods and portion control</p>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="space-y-4">
                        <h4 className="text-lg font-bold text-red-400">📺 Recommended Videos</h4>
                        <div className="space-y-3">
                          <div className="bg-red-600/20 p-4 rounded-xl border border-red-500/30">
                            <h5 className="text-white font-bold mb-2">🔥 Morning Fat Burn Workout</h5>
                            <p className="text-red-200 text-sm mb-3">10-minute high-intensity routine</p>
                            <a 
                              href="https://www.youtube.com/@Fitness_coach_129" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors inline-flex items-center space-x-2"
                            >
                              <FaPlay />
                              <span>Watch Now</span>
                            </a>
                          </div>
                          
                          <div className="bg-green-600/20 p-4 rounded-xl border border-green-500/30">
                            <h5 className="text-white font-bold mb-2">🥗 Healthy Meal Prep</h5>
                            <p className="text-green-200 text-sm mb-3">Easy recipes for your goals</p>
                            <a 
                              href="https://www.youtube.com/@Fitness_coach_129" 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors inline-flex items-center space-x-2"
                            >
                              <FaPlay />
                              <span>Watch Now</span>
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* PDF Download Info */}
                    <div className="mt-6 p-4 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-xl border border-blue-500/30">
                      <div className="flex items-center space-x-3 mb-2">
                        <FaFilePdf className="text-red-400 text-xl" />
                        <h5 className="text-white font-bold">Get Your Complete BMI Report</h5>
                      </div>
                      <p className="text-gray-300 text-sm mb-3">
                        Download a comprehensive PDF report with your BMI analysis, personalized recommendations, 
                        and fitness tips from Rugged Fitness Coach Sabari.
                      </p>
                      <div className="flex flex-wrap gap-2 text-xs text-blue-200">
                        <span className="bg-blue-600/30 px-2 py-1 rounded">✓ Detailed Analysis</span>
                        <span className="bg-blue-600/30 px-2 py-1 rounded">✓ Personal Recommendations</span>
                        <span className="bg-blue-600/30 px-2 py-1 rounded">✓ Fitness Tips</span>
                        <span className="bg-blue-600/30 px-2 py-1 rounded">✓ YouTube Channel Info</span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                /* Welcome Message */
                <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl p-8 text-white text-center shadow-2xl">
                  <div className="mb-6">
                    <img
                      src={channelLogo}
                      alt="Rugged Fitness Coach Sabari"
                      className="w-24 h-24 rounded-full mx-auto border-4 border-white shadow-lg"
                    />
                  </div>
                  <h3 className="text-3xl font-bold mb-4">Welcome to Your Fitness Journey! 🚀</h3>
                  <p className="text-purple-100 mb-6 text-lg">
                    Calculate your BMI and get personalized recommendations to transform your body and mind with Rugged Fitness Coach Sabari.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="bg-white/20 backdrop-blur-sm p-4 rounded-xl">
                      <div className="text-2xl mb-2">🎯</div>
                      <p className="font-bold">Personalized Plans</p>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm p-4 rounded-xl">
                      <div className="text-2xl mb-2">📊</div>
                      <p className="font-bold">Track Progress</p>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm p-4 rounded-xl">
                      <div className="text-2xl mb-2">💪</div>
                      <p className="font-bold">Get Results</p>
                    </div>
                  </div>
                  <p className="text-pink-200 font-bold">
                    👈 Fill in your details and let's get started!
                  </p>
                </div>
              )}

              {/* YouTube Channel Promotion */}
              <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-8 border border-red-500/20 shadow-2xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                  <div>
                    <div className="flex items-center space-x-4 mb-4">
                      <img
                        src={channelLogo}
                        alt="Rugged Fitness Coach Sabari"
                        className="w-16 h-16 rounded-full border-2 border-white"
                      />
                      <div>
                        <h3 className="text-2xl font-bold text-white">Rugged Fitness Coach Sabari</h3>
                        <p className="text-red-100">Your transformation partner!</p>
                      </div>
                    </div>
                    <p className="text-red-100 mb-4 text-sm">
                      🏃‍♀️ Rugged home workouts • 🥗 Power nutrition guides • 💪 Hardcore motivation & tips
                    </p>
                    <div className="flex flex-wrap gap-3">
                      <a 
                        href="https://www.youtube.com/@Fitness_coach_129" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="bg-white text-red-600 px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors flex items-center space-x-2"
                      >
                        <FaYoutube />
                        <span>Subscribe Now</span>
                      </a>
                      <button className="bg-red-800 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-bold hover:bg-red-900 transition-colors flex items-center space-x-2">
                        <FaBell />
                        <span>Notifications</span>
                      </button>
                    </div>
                  </div>
                  
                  <div className="text-center">
                    <img
                      src={transformPhoto}
                      alt="Transformation"
                      className="w-48 h-64 object-cover rounded-xl mx-auto border-4 border-white/20 shadow-2xl"
                    />
                    <p className="text-red-100 mt-4 font-bold">Your transformation awaits! 🔥</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Other tabs content */}
        {activeTab === 'members' && (
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
              <FaUser className="mr-3 text-yellow-400" />
              Gym Members Management
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 rounded-xl text-white">
                <h3 className="text-lg font-bold mb-2">Total Members</h3>
                <p className="text-3xl font-bold">247</p>
                <p className="text-green-100 text-sm">+12 this month</p>
              </div>
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-xl text-white">
                <h3 className="text-lg font-bold mb-2">Active Today</h3>
                <p className="text-3xl font-bold">89</p>
                <p className="text-blue-100 text-sm">36% of total</p>
              </div>
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-6 rounded-xl text-white">
                <h3 className="text-lg font-bold mb-2">New This Week</h3>
                <p className="text-3xl font-bold">8</p>
                <p className="text-purple-100 text-sm">Great progress!</p>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'workouts' && (
          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                <FaFire className="mr-3 text-orange-400" />
                Popular Workout Plans
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { name: 'Beginner Full Body', duration: '30 min', difficulty: 'Easy', color: 'from-green-500 to-green-600' },
                  { name: 'HIIT Cardio Blast', duration: '20 min', difficulty: 'Hard', color: 'from-red-500 to-red-600' },
                  { name: 'Strength Training', duration: '45 min', difficulty: 'Medium', color: 'from-blue-500 to-blue-600' },
                  { name: 'Yoga & Flexibility', duration: '35 min', difficulty: 'Easy', color: 'from-purple-500 to-purple-600' },
                  { name: 'Core Crusher', duration: '25 min', difficulty: 'Medium', color: 'from-yellow-500 to-orange-500' },
                  { name: 'Upper Body Power', duration: '40 min', difficulty: 'Hard', color: 'from-indigo-500 to-indigo-600' }
                ].map((workout, index) => (
                  <div key={index} className={`bg-gradient-to-r ${workout.color} p-6 rounded-xl text-white`}>
                    <h3 className="font-bold text-lg mb-2">{workout.name}</h3>
                    <p className="text-sm opacity-90 mb-1">Duration: {workout.duration}</p>
                    <p className="text-sm opacity-90 mb-4">Level: {workout.difficulty}</p>
                    <button className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg text-sm font-medium transition-colors w-full sm:w-auto">
                      Start Workout
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* YouTube Integration */}
            <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-8 border border-red-500/20">
              <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                <FaYoutube className="mr-3" />
                Latest Fitness Videos
              </h3>
              <p className="text-red-100 mb-6">
                Check out the latest workout videos from Rugged Fitness Coach Sabari! 
                Get motivated with easy home workouts and fitness tips.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-black/20 p-4 rounded-lg">
                  <h4 className="text-white font-bold mb-2">🔥 10-Minute Morning Workout</h4>
                  <p className="text-red-100 text-sm mb-3">Perfect way to start your day with energy!</p>
                  <button className="bg-white text-red-600 px-4 py-2 rounded-lg font-bold hover:bg-gray-100 transition-colors w-full sm:w-auto">
                    Watch Now
                  </button>
                </div>
                <div className="bg-black/20 p-4 rounded-lg">
                  <h4 className="text-white font-bold mb-2">🥗 Healthy Meal Prep Guide</h4>
                  <p className="text-red-100 text-sm mb-3">Easy recipes for your fitness journey!</p>
                  <button className="bg-white text-red-600 px-4 py-2 rounded-lg font-bold hover:bg-gray-100 transition-colors w-full sm:w-auto">
                    Watch Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'nutrition' && (
          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                <FaHeart className="mr-3 text-red-400" />
                Nutrition Guide & Meal Planning
              </h2>
              
              {bmiResult && (
                <div className="bg-gradient-to-r from-green-500/20 to-blue-500/20 p-6 rounded-xl mb-6">
                  <h3 className="text-xl font-bold text-white mb-4">Your Personalized Nutrition Plan</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-yellow-400">{bmiResult.calories}</p>
                      <p className="text-gray-300">Daily Calories</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-400">{Math.round(bmiResult.calories * 0.3 / 4)}g</p>
                      <p className="text-gray-300">Protein</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-400">{Math.round(bmiResult.calories * 0.45 / 4)}g</p>
                      <p className="text-gray-300">Carbs</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-white">🥗 Healthy Meal Ideas</h3>
                  {[
                    { meal: 'Breakfast', food: 'Oatmeal with berries and nuts', calories: '350 cal' },
                    { meal: 'Lunch', food: 'Grilled chicken salad', calories: '450 cal' },
                    { meal: 'Snack', food: 'Greek yogurt with honey', calories: '150 cal' },
                    { meal: 'Dinner', food: 'Salmon with quinoa and vegetables', calories: '500 cal' }
                  ].map((item, index) => (
                    <div key={index} className="bg-white/5 p-4 rounded-lg">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-yellow-400 font-bold">{item.meal}</p>
                          <p className="text-white">{item.food}</p>
                        </div>
                        <p className="text-gray-300 text-sm">{item.calories}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-white">💡 Nutrition Tips</h3>
                  <div className="space-y-3">
                    {[
                      'Drink at least 8 glasses of water daily',
                      'Eat protein with every meal',
                      'Include colorful vegetables in your diet',
                      'Avoid processed and sugary foods',
                      'Plan your meals in advance',
                      'Listen to your body\'s hunger cues'
                    ].map((tip, index) => (
                      <div key={index} className="bg-white/5 p-3 rounded-lg">
                        <p className="text-gray-300 text-sm">• {tip}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GymManagement;